interface Snapshot<T> {
  timestamp: number;
  state: T;
}

/**
 * Buffers server snapshots (20Hz patches) and samples them slightly in the
 * past so movement renders smoothly regardless of network jitter.
 */
export class StateInterpolator<T extends Record<string, number>> {
  private buffer: Snapshot<T>[] = [];
  private interpolationDelay: number;

  constructor(interpolationDelayMs: number = 120) {
    this.interpolationDelay = interpolationDelayMs;
  }

  pushSnapshot(state: T): void {
    this.buffer.push({
      timestamp: performance.now(),
      state: { ...state },
    });

    if (this.buffer.length > 12) {
      this.buffer.shift();
    }
  }

  getInterpolatedState(): T | null {
    if (this.buffer.length === 0) return null;
    if (this.buffer.length === 1) return { ...this.buffer[0].state };

    const renderTime = performance.now() - this.interpolationDelay;

    let older: Snapshot<T> | null = null;
    let newer: Snapshot<T> | null = null;

    for (let i = 0; i < this.buffer.length - 1; i++) {
      if (
        this.buffer[i].timestamp <= renderTime &&
        this.buffer[i + 1].timestamp >= renderTime
      ) {
        older = this.buffer[i];
        newer = this.buffer[i + 1];
        break;
      }
    }

    if (!older || !newer) {
      return { ...this.buffer[this.buffer.length - 1].state };
    }

    const range = newer.timestamp - older.timestamp;
    const alpha = range > 0 ? (renderTime - older.timestamp) / range : 0;

    const result = { ...older.state };
    for (const key in result) {
      const oldVal = older.state[key];
      const newVal = newer.state[key];
      (result as Record<string, number>)[key] = oldVal + (newVal - oldVal) * alpha;
    }

    return result;
  }
}
